// Thin fetch wrapper — adds the bearer token, parses JSON, raises on !ok.
//
// Same-origin in prod (`dreambook.studio/api/*`); dev uses Vite's /api proxy
// to the local FastAPI on :8402. No base URL needed.

import { getToken, clearToken } from "./auth";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public body?: unknown,
  ) {
    super(`${status} ${detail}`);
  }
}

type Json = Record<string, unknown> | Array<unknown> | null;

// v2.29 — only the canonical "am I logged in" endpoint is authoritative for
// session expiry. A 401 from other endpoints means "this specific request
// failed auth" (admin-only, ownership, etc.), not "your session is dead."
function _isAuthCheckPath(path: string): boolean {
  return path === "/api/auth/me" || path.startsWith("/api/auth/me?");
}

async function request<T>(
  method: string,
  path: string,
  body?: Json,
  opts: { auth?: boolean } = { auth: true },
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.auth !== false) {
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }

  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // v2.29 — only the canonical auth endpoint (/api/auth/me) is authoritative
  // for "session is dead". Other endpoints can return 401 for non-session
  // reasons (admin-only, ownership check, missing permissions) — wiping the
  // token globally on any of those caused a login-loop bug: a non-admin user
  // hitting an admin-only endpoint (e.g. /api/admin/imprint/diagnostic from
  // the wizard) had their session cleared and was bounced to /login on the
  // next refresh. Narrow the clearToken trigger to only the canonical check.
  if (res.status === 401 && opts.auth !== false && _isAuthCheckPath(path)) {
    clearToken();
  }

  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    throw new ApiError(res.status, extractDetail(parsed, res.statusText), parsed);
  }
  return parsed as T;
}

// Multipart form-data upload. Different from `post()` because we must NOT
// set Content-Type ourselves — the browser sets it with the correct
// multipart boundary. Used for audio-brief upload, image uploads, etc.
async function requestForm<T>(
  path: string,
  form: FormData,
  opts: { auth?: boolean } = { auth: true },
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.auth !== false) {
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }
  const res = await fetch(path, { method: "POST", headers, body: form });
  if (res.status === 401 && opts.auth !== false && _isAuthCheckPath(path)) clearToken();
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    throw new ApiError(res.status, extractDetail(parsed, res.statusText), parsed);
  }
  return parsed as T;
}

/** Pull a human-readable message out of a FastAPI error body.
 *  FastAPI may return:
 *    {"detail": "plain string"}        ← 400, 402, custom HTTPExceptions
 *    {"detail": [{"loc": [...], "msg": "..."}, …]}  ← 422 validation
 *    {"detail": {"error": "...", ...}} ← custom structured detail
 *  We flatten any of those to a readable sentence rather than letting
 *  JS call `String(obj)` and render "[object Object]" in the UI. */
function extractDetail(parsed: unknown, fallback: string): string {
  if (!parsed || typeof parsed !== "object") return fallback || "Request failed";
  const d = (parsed as { detail?: unknown }).detail;
  if (d == null) return fallback || "Request failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    // FastAPI 422 shape — take the first validation error's msg.
    const first = d[0] as { msg?: string; loc?: unknown[] } | undefined;
    if (first?.msg) {
      const where = Array.isArray(first.loc) ? first.loc.filter((p) => p !== "body").join(".") : "";
      return where ? `${where}: ${first.msg}` : first.msg;
    }
    try { return JSON.stringify(d[0]); } catch { return fallback; }
  }
  if (typeof d === "object") {
    const obj = d as Record<string, unknown>;
    if (typeof obj.message === "string") return obj.message;
    if (typeof obj.error === "string")   return obj.error;
    try { return JSON.stringify(d); } catch { return fallback; }
  }
  return fallback || "Request failed";
}

export const api = {
  get:     <T>(p: string, o?: { auth?: boolean }) => request<T>("GET", p, undefined, o),
  post:    <T>(p: string, b?: Json, o?: { auth?: boolean }) => request<T>("POST", p, b, o),
  put:     <T>(p: string, b?: Json, o?: { auth?: boolean }) => request<T>("PUT", p, b, o),
  patch:   <T>(p: string, b?: Json, o?: { auth?: boolean }) => request<T>("PATCH", p, b, o),
  del:     <T>(p: string, o?: { auth?: boolean }) => request<T>("DELETE", p, undefined, o),
  delete:  <T>(p: string, o?: { auth?: boolean }) => request<T>("DELETE", p, undefined, o),
  postForm: requestForm,
};
